<template>
  <!-- Label with required/optional indicator -->
  <label
    v-if="props.label"
    class="u-block u-textBlackAlt u-spaceBottom04"
    :class="labelClass"
    :for="props.id"
  >
    {{ props.label }}
    <!-- Required field indicator with asterisk -->
    <span v-if="props.required"
      ><span class="u-hiddenVisually">(required)</span><sup class="u-textRed">*</sup></span
    >
    <!-- Optional field indicator for screen readers -->
    <span v-else class="u-hiddenVisually">(optional)</span>
  </label>

  <!-- NPI Input field -->
  <input
    :class="`Input ${props.inputClass} ${props.required ? 'is-required' : ''} ${errorMessage ? 'u-borderColorRed' : ''}`"
    :id="props.id"
    :name="props.name || props.id"
    :required="props.required"
    :value="fieldValue"
    type="text"
    :placeholder="props.placeholder"
    maxlength="10"
    pattern="^\d{10}$"
    :aria-describedby="props.id + 'Help'"
    :disabled="props.disabled || false"
    v-bind="$attrs"
    @input="handleInput"
    @blur="handleBlur"
  />

  <!-- Help text for additional context -->
  <small
    class="u-block u-textShrink2 u-textGrey u-spaceTop01"
    v-if="props.helpText"
    :id="props.id + 'Help'"
    >{{ props.helpText }}</small
  >

  <!-- Validation error message -->
  <p class="u-textShrink1 u-textRed u-spaceTop02" v-if="errorMessage" role="alert">
    {{ errorMessage }}
  </p>
</template>

<script setup>
import { defineProps, defineEmits, computed, watch, useAttrs } from 'vue'
import { useField } from 'vee-validate'

// Prevent attribute inheritance to manually control where they're applied
defineOptions({ inheritAttrs: false })

const attrs = useAttrs()

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  id: {
    type: String,
    required: true,
  },
  name: {
    type: String,
    default: '',
  },
  label: {
    type: String,
    default: 'NPI Number',
  },
  placeholder: {
    type: String,
    default: '1234567890',
  },
  helpText: {
    type: String,
    default: 'Enter a 10-digit National Provider Identifier',
  },
  required: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  labelClass: {
    type: String,
    default: '',
  },
  inputClass: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['update:modelValue', 'blur'])

// NPI-specific Luhn algorithm implementation
// Based on CMS NPI validation specification
const luhnCheck = (npiString) => {
  if (!npiString || npiString.length !== 10) {
    return false
  }

  // NPI validation follows this process:
  // 1. Prefix the NPI with "80840" to create a 15-digit number
  // 2. Apply the Luhn algorithm to this 15-digit number
  // 3. The last digit should make the sum divisible by 10

  const prefixedNPI = '80840' + npiString
  const digits = prefixedNPI.split('').map(Number)

  let sum = 0
  let shouldDouble = false

  // Process digits from right to left
  for (let i = digits.length - 1; i >= 0; i--) {
    let digit = digits[i]

    // Double every second digit from the right
    if (shouldDouble) {
      digit *= 2
      // If doubling results in a two-digit number, add the digits
      if (digit > 9) {
        digit -= 9 // Equivalent to Math.floor(digit/10) + (digit%10)
      }
    }

    sum += digit
    shouldDouble = !shouldDouble
  }

  // The NPI is valid if the sum is divisible by 10
  return sum % 10 === 0
}

// NPI-specific validation rules
const npiValidationRules = computed(() => {
  const rules = []

  // Required field validation
  if (props.required) {
    rules.push((value) => {
      if (
        value === undefined ||
        value === null ||
        value === '' ||
        (typeof value === 'string' && value.trim() === '')
      ) {
        return 'NPI number is required'
      }
      return true
    })
  }

  // Basic format validation
  rules.push((value) => {
    if (!value) return true // Let required rule handle empty values

    // Check if it contains only digits
    if (!/^\d+$/.test(value)) {
      return 'NPI must contain only numbers'
    }

    // Check length
    if (value.length < 10) {
      return 'NPI must be exactly 10 digits'
    }

    if (value.length > 10) {
      return 'NPI cannot be more than 10 digits'
    }

    return true
  })

  // Luhn algorithm validation
  rules.push((value) => {
    if (!value || value.length !== 10) return true // Let format validation handle this

    if (!luhnCheck(value)) {
      return 'Invalid NPI number - please verify the digits'
    }

    return true
  })

  return rules
})

// Vee-validate field setup
const {
  errorMessage,
  value: fieldValue,
  handleBlur: veeHandleBlur,
  handleChange,
  validate,
} = useField(props.name || props.id, npiValidationRules, {
  initialValue: props.modelValue,
  validateOnValueUpdate: false,
})

// Input event handler
const handleInput = (event) => {
  const newValue = event.target.value
  // Only allow digits
  const digitsOnly = newValue.replace(/\D/g, '')
  // Limit to 10 digits
  const limitedValue = digitsOnly.slice(0, 10)

  // Update the input value to show only digits
  event.target.value = limitedValue

  handleChange(limitedValue)
  emit('update:modelValue', limitedValue)
}

// Blur event handler
const handleBlur = async (event) => {
  await validate()
  veeHandleBlur(event)
  emit('blur', event)
}

// Prop-to-field synchronization
watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue !== fieldValue.value) {
      handleChange(newValue)
    }
  },
)
</script>
